import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { createProductionHandover } from '../../../../api/productionHandoverApi';

const emptyForm = {
  productionOrder: '',
  materialNumberSnapshot: '',
  productNameSnapshot: '',
  batch: '',
  productionLine: '',
};

const getApiErrorMessage = (error) => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  if (typeof responseMessage === 'string') {
    return responseMessage;
  }

  return 'Nie udało się dodać zlecenia produkcyjnego.';
};

const detectProductionLine = (batch) => {
  const normalizedBatch = String(batch ?? '').trim();

  if (normalizedBatch.length < 2) {
    return '';
  }

  const detectedLine = normalizedBatch.charAt(1);

  return /^[1-7]$/.test(detectedLine) ? detectedLine : '';
};

const CreateProductionHandoverDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(emptyForm);
    setError('');
  }, [open]);

  const clearError = () => {
    if (error) {
      setError('');
    }
  };

  const handleFieldChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    clearError();
  };

  const handleBatchChange = (value) => {
    const detectedLine = detectProductionLine(value);

    setForm((previous) => ({
      ...previous,
      batch: value,
      productionLine: detectedLine || previous.productionLine,
    }));

    clearError();
  };

  const validateForm = () => {
    const productionOrder = form.productionOrder.trim();

    const materialNumberSnapshot = Number(form.materialNumberSnapshot);

    const productNameSnapshot = form.productNameSnapshot.trim();

    const batch = form.batch.trim();

    const productionLine = form.productionLine.trim();

    if (!productionOrder) {
      return 'Podaj numer zlecenia produkcyjnego.';
    }

    if (productionOrder.length > 30) {
      return 'Numer zlecenia może zawierać maksymalnie 30 znaków.';
    }

    if (
      !Number.isInteger(materialNumberSnapshot) ||
      materialNumberSnapshot <= 0
    ) {
      return 'Podaj prawidłowy numer materiału.';
    }

    if (!Number.isSafeInteger(materialNumberSnapshot)) {
      return 'Numer materiału jest zbyt duży.';
    }

    if (!productNameSnapshot) {
      return 'Podaj nazwę materiału.';
    }

    if (productNameSnapshot.length > 255) {
      return 'Nazwa materiału może zawierać maksymalnie 255 znaków.';
    }

    if (batch.length < 2) {
      return 'Partia musi zawierać co najmniej 2 znaki.';
    }

    if (batch.length > 50) {
      return 'Partia może zawierać maksymalnie 50 znaków.';
    }

    if (!/^[1-7]$/.test(productionLine)) {
      return 'Wybierz linię produkcyjną od 1 do 7.';
    }

    const lineFromBatch = detectProductionLine(batch);

    if (!lineFromBatch) {
      return (
        'Nie można rozpoznać linii produkcyjnej z partii. ' +
        'Drugi znak partii musi mieć wartość od 1 do 7.'
      );
    }

    if (lineFromBatch !== productionLine) {
      return (
        `Drugi znak partii wskazuje linię ${lineFromBatch}, ` +
        `natomiast wybrano linię ${productionLine}.`
      );
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      productionOrder: form.productionOrder.trim(),

      materialNumberSnapshot: Number(form.materialNumberSnapshot),

      productNameSnapshot: form.productNameSnapshot.trim(),

      batch: form.batch.trim(),

      productionLine: form.productionLine.trim(),
    };

    setLoading(true);
    setError('');

    try {
      const createdHandover = await createProductionHandover(payload);

      setLoading(false);
      setForm(emptyForm);

      if (onCreated) {
        onCreated(createdHandover);
      }
    } catch (requestError) {
      console.error(requestError);

      setError(getApiErrorMessage(requestError));

      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setError('');
    setForm(emptyForm);

    if (onClose) {
      onClose();
    }
  };

  const detectedLine = detectProductionLine(form.batch);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dodaj zlecenie produkcyjne</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Zlecenie zostanie utworzone ze statusem „Dostępne” i będzie można
            przekazać je do analizy przez bota.
          </DialogContentText>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Numer zlecenia produkcyjnego"
              value={form.productionOrder}
              onChange={(event) =>
                handleFieldChange('productionOrder', event.target.value)
              }
              disabled={loading}
              required
              autoFocus
              fullWidth
              inputProps={{
                maxLength: 30,
              }}
            />

            <TextField
              label="Numer materiału"
              value={form.materialNumberSnapshot}
              onChange={(event) => {
                const sanitizedValue = event.target.value.replace(/\D/g, '');

                handleFieldChange('materialNumberSnapshot', sanitizedValue);
              }}
              disabled={loading}
              required
              fullWidth
              inputProps={{
                inputMode: 'numeric',
                maxLength: 10,
              }}
            />

            <TextField
              label="Nazwa materiału"
              value={form.productNameSnapshot}
              onChange={(event) =>
                handleFieldChange('productNameSnapshot', event.target.value)
              }
              disabled={loading}
              required
              fullWidth
              inputProps={{
                maxLength: 255,
              }}
            />

            <TextField
              label="Partia"
              value={form.batch}
              onChange={(event) => handleBatchChange(event.target.value)}
              disabled={loading}
              required
              fullWidth
              inputProps={{
                maxLength: 50,
              }}
              error={form.batch.length >= 2 && !detectedLine}
              helperText={
                form.batch.length >= 2 && !detectedLine
                  ? 'Drugi znak partii musi wskazywać linię od 1 do 7.'
                  : detectedLine
                  ? `Na podstawie partii wykryto linię ${detectedLine}.`
                  : 'Drugi znak partii określa linię produkcyjną.'
              }
            />

            <FormControl
              fullWidth
              required
              disabled={loading}
              error={
                Boolean(form.productionLine) &&
                Boolean(detectedLine) &&
                form.productionLine !== detectedLine
              }
            >
              <InputLabel>Linia produkcyjna</InputLabel>

              <Select
                label="Linia produkcyjna"
                value={form.productionLine}
                onChange={(event) =>
                  handleFieldChange('productionLine', event.target.value)
                }
              >
                {[1, 2, 3, 4, 5, 6, 7].map((line) => (
                  <MenuItem key={line} value={String(line)}>
                    Linia {line}
                  </MenuItem>
                ))}
              </Select>

              <FormHelperText>
                {form.productionLine &&
                detectedLine &&
                form.productionLine !== detectedLine
                  ? `Partia wskazuje linię ${detectedLine}.`
                  : 'Pole jest automatycznie uzupełniane na podstawie partii.'}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Anuluj
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              !form.productionOrder.trim() ||
              !form.materialNumberSnapshot ||
              !form.productNameSnapshot.trim() ||
              form.batch.trim().length < 2 ||
              !form.productionLine
            }
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
          >
            {loading ? 'Dodawanie...' : 'Dodaj zlecenie'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateProductionHandoverDialog;
